import { useEffect, useRef } from 'react';

/**
 * Keep the device awake while this component is mounted.
 *
 * Uses the Screen Wake Lock API (`navigator.wakeLock.request('screen')`).
 * The browser automatically releases the lock when the tab goes hidden,
 * so we listen for `visibilitychange` and re-acquire on return.
 *
 * Silently no-ops when the API is unsupported (older Safari, some embeds)
 * or when the request is rejected (no user gesture, low battery, OS policy).
 * Phones at the table get an explicit user tap on the login flow before
 * this hook runs, which satisfies the gesture requirement on every browser
 * that ships the API today.
 */
export function useWakeLock(active = true) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;

    let cancelled = false;

    const acquire = async () => {
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (cancelled) {
          sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
        // Spec: the sentinel auto-releases when the page hides. The release
        // event fires either way; we just clear our ref.
        sentinel.addEventListener('release', () => {
          if (sentinelRef.current === sentinel) sentinelRef.current = null;
        });
      } catch {
        // Ignored — feature is best-effort. App still works without it.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !sentinelRef.current) {
        acquire();
      }
    };

    acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s) s.release().catch(() => {});
    };
  }, [active]);
}
